import { Plus, Search, Trash2 } from "lucide-react"
import { Button, NumberControl } from "../../../ui"
import { formatVND, PAYMENT_TYPE_PICKER_OPTIONS } from "../../../constants"
import GrStatusBadge from "./GrStatusBadge"
import { HScroll } from "procurement/design/components/HScroll"

const money = (n) => (n == null || n === "" ? "—" : formatVND(n))

// One PO per row with its amount/% pair. The invoice belongs to the whole
// request and lives in its own section below the table.
const PoPaymentTable = ({
  rows,
  filter,
  onFilter,
  showErrors,
  errorsById,
  disabled,
  paymentType,
  onPaymentType,
  canAdd,
  onAdd,
  onToggleAll,
  allIncluded,
  onToggleIncluded,
  onRemove,
  onAmount,
  onPercent,
  onMode,
  onField,
}) => {
  const query = filter.trim().toLowerCase()
  const visible = rows.filter((r) => `${r.poCode} ${r.supplierName}`.toLowerCase().includes(query))

  return (
    <>
      <div className="prc-toolbar">
        <div className="prc-search">
          <Search aria-hidden />
          <input
            value={filter}
            onChange={(e) => onFilter(e.target.value)}
            placeholder="Search PO number, supplier..."
            aria-label="Search selected purchase orders"
          />
        </div>
        <label className="prc-paytype">
          Payment type
          <select
            className="prc-select"
            value={paymentType}
            onChange={(e) => onPaymentType(e.target.value)}
            disabled={disabled}
            aria-label="Payment type"
          >
            {PAYMENT_TYPE_PICKER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {canAdd && (
          <Button variant="primary" size="sm" leftIcon={<Plus aria-hidden />} onClick={onAdd} disabled={disabled}>
            Add POs
          </Button>
        )}
      </div>
      <HScroll className="prc-table-wrap">
        <table className="prc-table">
          <thead>
            <tr>
              <th className="prc-check">
                <input
                  type="checkbox"
                  checked={allIncluded}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  disabled={disabled || rows.length === 0}
                  aria-label="Include all purchase orders"
                />
              </th>
              <th className="prc-idx">#</th>
              <th>PO Number</th>
              <th>Supplier</th>
              <th className="prc-num">PO Total</th>
              <th className="prc-num">Previously Paid</th>
              <th className="prc-num">Remaining</th>
              <th>Goods Receipt Status</th>
              <th>Payment Amount</th>
              <th>%</th>
              <th>Note</th>
              <th className="prc-actions" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} className="prc-empty">
                  No purchase orders yet. Add POs and enter how much to pay on each.
                </td>
              </tr>
            )}
            {rows.length > 0 && visible.length === 0 && (
              <tr>
                <td colSpan={12} className="prc-empty">
                  No selected purchase orders match “{filter}”.
                </td>
              </tr>
            )}
            {visible.map((row) => {
              const errors = (showErrors && errorsById?.[row.purchaseId]) || {}
              const index = rows.findIndex((r) => r.purchaseId === row.purchaseId) + 1
              const percentMode = row.inputMode === "percent"
              const locked = row.amountsLocked || disabled
              return (
                <tr key={row.purchaseId}>
                  <td className="prc-check">
                    <input
                      type="checkbox"
                      checked={!!row.included}
                      onChange={(e) => onToggleIncluded(row.purchaseId, e.target.checked)}
                      disabled={disabled}
                      aria-label={`Include ${row.poCode || row.purchaseId}`}
                    />
                  </td>
                  <td className="prc-idx">{index}</td>
                  <td className="prc-po">{row.poCode || `PO #${row.purchaseId}`}</td>
                  <td>{row.supplierName || "—"}</td>
                  <td className="prc-num">{money(row.total)}</td>
                  <td className="prc-num">{money(row.previouslyPaid)}</td>
                  <td className="prc-num">{money(row.remaining)}</td>
                  <td>
                    <GrStatusBadge purchaseId={row.purchaseId} />
                  </td>
                  <td className="prc-money">
                    <div className="prc-seg" role="group" aria-label={`Amount or percent for ${row.poCode || row.purchaseId}`}>
                      <button
                        type="button"
                        className={!percentMode ? "is-on" : ""}
                        disabled={locked}
                        onClick={() => onMode(row.purchaseId, "amount")}
                      >
                        Amount
                      </button>
                      <button
                        type="button"
                        className={percentMode ? "is-on" : ""}
                        disabled={locked}
                        onClick={() => onMode(row.purchaseId, "percent")}
                      >
                        %
                      </button>
                    </div>
                    <NumberControl
                      className={`prc-input${errors.amount ? " is-invalid" : ""}`}
                      inputMode="numeric"
                      value={row.amount}
                      readOnly={percentMode || locked}
                      aria-label={`Payment amount for ${row.poCode || row.purchaseId}`}
                      onChange={(v) => onAmount(row.purchaseId, v)}
                    />
                    {errors.amount && <span className="prc-field-error">{errors.amount}</span>}
                  </td>
                  <td className="prc-pct">
                    <input
                      className={`prc-input${errors.percent ? " is-invalid" : ""}`}
                      inputMode="decimal"
                      value={row.percent === "" || row.percent == null ? "" : row.percent}
                      readOnly={!percentMode || locked}
                      aria-label={`Payment percent for ${row.poCode || row.purchaseId}`}
                      onChange={(e) => onPercent(row.purchaseId, e.target.value)}
                    />
                    {errors.percent && !errors.amount && <span className="prc-field-error">{errors.percent}</span>}
                  </td>
                  <td className="prc-note">
                    <input
                      className="prc-input"
                      value={row.note}
                      title={row.note || ""}
                      placeholder="Note"
                      aria-label={`Note for ${row.poCode || row.purchaseId}`}
                      disabled={disabled}
                      onChange={(e) => onField(row.purchaseId, { note: e.target.value })}
                    />
                  </td>
                  <td className="prc-actions">
                    {canAdd && (
                      <button
                        type="button"
                        className="prc-icon-btn"
                        aria-label={`Remove ${row.poCode || row.purchaseId}`}
                        disabled={disabled}
                        onClick={() => onRemove(row.purchaseId)}
                      >
                        <Trash2 aria-hidden />
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </HScroll>
      {canAdd && (
        <div className="prc-add-row">
          <Button variant="secondary" size="sm" leftIcon={<Plus aria-hidden />} onClick={onAdd} disabled={disabled}>
            Add another PO
          </Button>
        </div>
      )}

    </>
  )
}

export default PoPaymentTable
